import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { Calendar, Clock, ArrowLeft, Facebook, Twitter, Linkedin, Mail, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { BlogContactForm } from "@/components/blog/BlogContactForm";
import { SocialShare } from "@/components/blog/SocialShare";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string | null;
  author: string;
  author_bio: string | null;
  author_image: string | null;
  publish_date: string;
  reading_time: number | null;
  category_id: string | null;
  meta_title: string | null;
  meta_description: string | null;
  canonical_url: string | null;
  og_image: string | null;
  blog_categories: {
    name: string;
    slug: string;
  } | null;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const fetchPost = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select(`
        *,
        blog_categories (name, slug)
      `)
      .eq("slug", slug)
      .eq("status", "published")
      .maybeSingle();

    if (error) {
      toast.error("Failed to load article");
      setIsLoading(false);
      return;
    }

    if (!data) {
      toast.error("Article not found");
      setIsLoading(false);
      return;
    }

    setPost(data);
    fetchRelatedPosts(data.category_id, data.id);
    setIsLoading(false);
  };

  const fetchRelatedPosts = async (categoryId: string | null, currentPostId: string) => {
    if (!categoryId) return;

    const { data } = await supabase
      .from("blog_posts")
      .select(`
        *,
        blog_categories (name, slug)
      `)
      .eq("category_id", categoryId)
      .eq("status", "published")
      .neq("id", currentPostId)
      .limit(3);

    if (data) {
      setRelatedPosts(data);
    }
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
            <Link to="/blog">
              <Button>Back to Blog</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageUrl = `https://ricksblackcarservicesantonio.com/blog/${post.slug}`;
  
  // Extract plain text from HTML content for word count and article body
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = post.content;
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  const wordCount = plainText.trim().split(/\s+/).length;

  // Article schema markup
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt || post.meta_description,
    "image": {
      "@type": "ImageObject",
      "url": post.featured_image || post.og_image || "https://ricksblackcarservicesantonio.com/og-image.jpg",
      "width": 1200,
      "height": 630,
      "caption": post.title
    },
    "author": {
      "@type": "Person",
      "name": post.author,
      ...(post.author_bio && { "description": post.author_bio }),
      ...(post.author_image && { 
        "image": {
          "@type": "ImageObject",
          "url": post.author_image,
          "caption": `Photo of ${post.author}`
        }
      })
    },
    "publisher": {
      "@type": "Organization",
      "name": "Rick's Black Car Service",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ricksblackcarservicesantonio.com/og-image.jpg",
        "width": 1200,
        "height": 630
      },
      "url": "https://ricksblackcarservicesantonio.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "addressCountry": "US"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-210-296-7409",
        "contactType": "Customer Service",
        "email": "ricksblackcarservice@outlook.com"
      }
    },
    "datePublished": post.publish_date,
    "dateModified": post.publish_date,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageUrl
    },
    "articleBody": plainText.substring(0, 5000),
    "wordCount": wordCount,
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "isPartOf": {
      "@type": "Blog",
      "@id": "https://ricksblackcarservicesantonio.com/blog#blog",
      "name": "Rick's Black Car Service Blog",
      "description": "Expert insights on luxury transportation, airport transfers, and travel tips in San Antonio"
    },
    ...(post.reading_time && { "timeRequired": `PT${post.reading_time}M` }),
    ...(post.blog_categories && {
      "articleSection": post.blog_categories.name,
      "genre": post.blog_categories.name,
      "keywords": `${post.blog_categories.name}, luxury transportation, black car service, San Antonio`
    })
  };

  // Breadcrumb schema markup
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://ricksblackcarservicesantonio.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://ricksblackcarservicesantonio.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": pageUrl
      }
    ]
  };

  // Featured image schema for enhanced image search
  const imageSchema = post.featured_image ? {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "url": post.featured_image,
    "caption": post.title,
    "description": post.excerpt || post.meta_description || `Featured image for ${post.title}`,
    "width": 1920,
    "height": 1080,
    "publisher": {
      "@type": "Organization",
      "name": "Rick's Black Car Service",
      "url": "https://ricksblackcarservicesantonio.com/"
    },
    "copyrightNotice": "© Rick's Black Car Service",
    "creditText": "Rick's Black Car Service",
    "isPartOf": {
      "@type": "BlogPosting",
      "@id": pageUrl
    }
  } : null;

  return (
    <>
      <Helmet>
        <title>{post.meta_title || `${post.title} | Rick's Black Car Service Blog`}</title>
        <meta
          name="description"
          content={post.meta_description || post.excerpt || ""}
        />
        <link rel="canonical" href={post.canonical_url || pageUrl} />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt || ""} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        {(post.og_image || post.featured_image) && (
          <meta property="og:image" content={post.og_image || post.featured_image || ""} />
        )}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        {imageSchema && (
          <script type="application/ld+json">
            {JSON.stringify(imageSchema)}
          </script>
        )}
      </Helmet>

      <Navbar />

      <main className="min-h-screen bg-background pt-20">
        {/* Breadcrumb */}
        <section className="container mx-auto px-4 py-6">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">
              Home
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-foreground transition-colors">
              Blog
            </Link>
            <span>/</span>
            <span className="text-foreground">{post.title}</span>
          </nav>
        </section>

        {/* Article Header */}
        <article className="container mx-auto px-4 pb-20">
          <div className="max-w-4xl mx-auto">
            {/* Category Badge */}
            {post.blog_categories && (
              <Link to={`/blog?category=${post.blog_categories.slug}`}>
                <Badge variant="secondary" className="mb-4">
                  {post.blog_categories.name}
                </Badge>
              </Link>
            )}

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {post.title}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {format(new Date(post.publish_date), "MMMM d, yyyy")}
              </div>
              {post.reading_time && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {post.reading_time} min read
                </div>
              )}
              <div>By {post.author}</div>
            </div>

            {/* Featured Image */}
            {post.featured_image && (
              <div className="aspect-video w-full overflow-hidden rounded-lg mb-8">
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Article Content */}
            <div 
              className="prose prose-lg max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
            />

            <Separator className="my-8" />

            {/* Author Bio */}
            {(post.author_bio || post.author_image) && (
              <Card className="mb-8">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    {post.author_image && (
                      <img
                        src={post.author_image}
                        alt={post.author}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <CardTitle className="text-xl">About {post.author}</CardTitle>
                      {post.author_bio && (
                        <p className="text-muted-foreground mt-2">{post.author_bio}</p>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            )}

            {/* Social Share */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Share this article</h3>
              <SocialShare url={pageUrl} title={post.title} />
            </div>

            {/* CTA */}
            <Card className="bg-primary/5 border-primary/20 mb-12">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Experience Luxury Transportation?</h3>
                <p className="text-muted-foreground mb-6">
                  Book your ride with San Antonio's premier black car service today.
                </p>
                <Link to="/book">
                  <Button size="lg">Book Your Ride Now</Button>
                </Link>
              </CardContent>
            </Card>

            {/* Contact Form */}
            <BlogContactForm postId={post.id} postTitle={post.title} />

            {/* Related Articles */}
            {relatedPosts.length > 0 && (
              <div className="mt-12">
                <h3 className="text-2xl font-bold mb-6">Related Articles</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedPosts.map((related) => (
                    <Card key={related.id} className="hover:shadow-lg transition-shadow">
                      {related.featured_image && (
                        <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                          <img
                            src={related.featured_image}
                            alt={related.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <CardContent className="p-4">
                        {related.blog_categories && (
                          <Badge variant="secondary" className="mb-2">
                            {related.blog_categories.name}
                          </Badge>
                        )}
                        <h4 className="font-semibold mb-2 line-clamp-2">
                          <Link
                            to={`/blog/${related.slug}`}
                            className="hover:text-primary transition-colors"
                          >
                            {related.title}
                          </Link>
                        </h4>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {related.excerpt}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Back to Blog */}
            <div className="mt-12">
              <Link to="/blog">
                <Button variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to All Articles
                </Button>
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}
